/**
 * Side-by-side comparison view
 * Shows 2-4 versions of the same artwork with different frame/mat configurations.
 */

import { useState, useMemo } from 'react';
import { Button, cn } from '@frameit/ui';
import { useProjectStore, useSelectedArtwork, getLocalImageUrl } from '../../stores/projectStore';
import { getFrameColor, FRAMES } from '../../data/frames';
import { getMatColor } from '../../data/mats';
import { buildCssFilter } from './utils/color-filters';
import { renderFrame } from './renderers/frame-renderer';
import type { ProjectFrame, ProjectMat } from '@frameit/types';

/** A single comparison slot configuration */
interface ComparisonSlot {
  id: string;
  label: string;
  frame: ProjectFrame;
  mat: ProjectMat | undefined;
}

/** Mini framed artwork preview for comparison */
function ComparisonPreview({
  imageUrl,
  frame,
  mat,
  colorFilter,
  maxSize = 200,
}: {
  imageUrl: string;
  frame: ProjectFrame;
  mat: ProjectMat | undefined;
  colorFilter: string;
  maxSize?: number;
}): React.JSX.Element {
  const frameColor = getFrameColor(frame.templateId);
  const frameWidth = (frame.width ?? 25) * 0.3;
  const matWidth = mat ? (mat.width ?? 0.1) * 120 : 0;
  const totalBorder = frameWidth + matWidth;
  const matColor = mat ? getMatColor(mat.templateId) : undefined;

  const frameImage = useMemo(() => {
    try {
      const innerW = Math.round(maxSize + matWidth * 2);
      const innerH = Math.round(maxSize + matWidth * 2);
      const canvas = renderFrame({
        templateId: frame.templateId,
        frameWidth: Math.round(frameWidth),
        innerWidth: innerW,
        innerHeight: innerH,
        highlightIntensity: frame.bevelAdjustments?.highlightIntensity,
        shadowIntensity: frame.bevelAdjustments?.shadowIntensity,
      });
      return canvas.toDataURL();
    } catch {
      return undefined;
    }
  }, [
    frame.templateId,
    frameWidth,
    maxSize,
    matWidth,
    frame.bevelAdjustments?.highlightIntensity,
    frame.bevelAdjustments?.shadowIntensity,
  ]);

  return (
    <div
      className="relative inline-block"
      style={{
        padding: `${totalBorder}px`,
        backgroundColor: frameColor,
        backgroundImage: frameImage ? `url(${frameImage})` : undefined,
        backgroundSize: '100% 100%',
        boxShadow: '3px 3px 10px rgba(0,0,0,0.25)',
      }}
    >
      {/* Mat */}
      {mat && matColor && (
        <div
          className="absolute"
          style={{
            inset: `${frameWidth}px`,
            backgroundColor: matColor,
            boxShadow:
              'inset 1px 1px 3px rgba(255,255,255,0.15), inset -1px -1px 2px rgba(0,0,0,0.1)',
          }}
        />
      )}

      {/* Artwork */}
      <img
        src={imageUrl}
        alt="Artwork"
        className="block"
        style={{
          maxWidth: maxSize,
          maxHeight: maxSize,
          objectFit: 'contain',
          filter: colorFilter !== 'none' ? colorFilter : undefined,
        }}
      />
    </div>
  );
}

/** Quick frame presets for comparison */
const COMPARISON_PRESETS: { label: string; frameId: string; matId?: string }[] = [
  { label: 'Classic Black', frameId: 'frame-001', matId: 'mat-white' },
  { label: 'Natural Oak', frameId: 'frame-002', matId: 'mat-cream' },
  { label: 'White Modern', frameId: 'frame-003' },
  { label: 'Gold Ornate', frameId: 'frame-004', matId: 'mat-ivory' },
  { label: 'Silver Metal', frameId: 'frame-033', matId: 'mat-light-grey' },
  { label: 'Walnut', frameId: 'frame-007', matId: 'mat-antique-white' },
  { label: 'Minimal Black', frameId: 'frame-047' },
  { label: 'Pine Natural', frameId: 'frame-025', matId: 'mat-linen' },
];

export interface ComparisonViewProps {
  onClose: () => void;
  className?: string;
}

/** Side-by-side comparison view */
export function ComparisonView({
  onClose,
  className,
}: ComparisonViewProps): React.JSX.Element | null {
  const selectedArtwork = useSelectedArtwork();
  const [slots, setSlots] = useState<ComparisonSlot[]>(() => {
    if (!selectedArtwork) return [];

    // Start with current configuration + 3 presets
    const initial: ComparisonSlot[] = [
      {
        id: 'current',
        label: 'Current',
        frame: { ...selectedArtwork.frame },
        mat: selectedArtwork.mat ? { ...selectedArtwork.mat } : undefined,
      },
    ];

    // Add up to 3 different presets
    const usedFrames = new Set([selectedArtwork.frame.templateId]);
    for (const preset of COMPARISON_PRESETS) {
      if (initial.length >= 4) break;
      if (usedFrames.has(preset.frameId)) continue;
      usedFrames.add(preset.frameId);

      const frameData = FRAMES.find((f) => f.id === preset.frameId);
      initial.push({
        id: `preset-${initial.length}`,
        label: preset.label,
        frame: {
          templateId: preset.frameId,
          width: frameData?.defaultWidth ?? 25,
        },
        mat: preset.matId ? { templateId: preset.matId, width: 0.1 } : undefined,
      });
    }

    return initial;
  });

  const updateFrame = useProjectStore((s) => s.updateFrame);
  const updateMat = useProjectStore((s) => s.updateMat);
  const saveToHistory = useProjectStore((s) => s.saveToHistory);

  if (!selectedArtwork) return null;

  const imageUrl = getLocalImageUrl(selectedArtwork.artworkId);
  if (!imageUrl) return null;

  const colorFilter = buildCssFilter(selectedArtwork.colorGrading);

  const handleApply = (slot: ComparisonSlot): void => {
    if (slot.id === 'current') return;
    saveToHistory();
    updateFrame(selectedArtwork.id, slot.frame);
    updateMat(selectedArtwork.id, slot.mat ?? null);
    onClose();
  };

  const handleChangeSlotFrame = (slotIndex: number, frameId: string): void => {
    const frameData = FRAMES.find((f) => f.id === frameId);
    if (!frameData) return;

    setSlots((prev) =>
      prev.map((s, i) =>
        i === slotIndex
          ? {
              ...s,
              label: frameData.name,
              frame: { templateId: frameId, width: frameData.defaultWidth },
            }
          : s,
      ),
    );
  };

  return (
    <div className={cn('absolute inset-0 z-30 bg-background/95 flex flex-col', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-medium text-sm">Compare Frames</h3>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </Button>
      </div>

      {/* Comparison grid */}
      <div className="flex-1 overflow-auto p-4">
        <div
          className={cn(
            'grid gap-4 h-full',
            slots.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 grid-rows-2',
          )}
        >
          {slots.map((slot, index) => (
            <div
              key={slot.id}
              className="flex flex-col items-center justify-center gap-3 p-3 rounded-lg border bg-muted/20"
            >
              {/* Preview */}
              <div className="flex-1 flex items-center justify-center">
                <ComparisonPreview
                  imageUrl={imageUrl}
                  frame={slot.frame}
                  mat={slot.mat}
                  colorFilter={colorFilter}
                  maxSize={slots.length <= 2 ? 250 : 160}
                />
              </div>

              {/* Label + frame selector */}
              <div className="text-center space-y-1.5 w-full">
                <p className="text-xs font-medium">{slot.label}</p>

                {/* Quick frame change */}
                {slot.id !== 'current' && (
                  <select
                    className="w-full text-xs px-2 py-1 rounded border bg-background"
                    value={slot.frame.templateId}
                    onChange={(e) => handleChangeSlotFrame(index, e.target.value)}
                  >
                    {FRAMES.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                )}

                {/* Apply button */}
                <Button
                  size="sm"
                  variant={slot.id === 'current' ? 'secondary' : 'default'}
                  className="w-full text-xs h-7"
                  onClick={() => handleApply(slot)}
                  disabled={slot.id === 'current'}
                >
                  {slot.id === 'current' ? 'Current' : 'Apply'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
